/**
 * Credit-cost calculation for the Phase 3b "Reveal selected" dropdown and the
 * Phase 5b XLSX dialog cost preview.
 *
 * Cost basis is set on the FE (1cr email, 10cr phone) because the BE
 * `bulk-reveal` and `download-preview` endpoints accept the field list and
 * return the authoritative cost in their response — but we need a synchronous
 * preview before that call so the dropdown can render N credits live as the
 * user toggles "Skip previously enriched".
 *
 * `skipPreviouslyEnriched` semantics (skip-aware, TD-2311):
 *   - true  → exclude contacts whose `enrichment_status_<field>` is already
 *             resolved (`'found'` or cached `'not_found'`) — those are free.
 *   - false → forced re-fetch: count every selected contact for every field,
 *             including ones already `'found'` or `'not_found'`. The BE
 *             re-queries and re-bills them (job `force_refetch`), so the preview
 *             must charge for them too.
 *
 * History: TD-2302 made `'not_found'` a terminal cached verdict; TD-2310 applied
 * that to BOTH skip states, which under-charged a forced re-fetch. TD-2311 makes
 * billing skip-aware — `'not_found'` is free only while skip is ON — and TD-2312
 * aligns this preview with it.
 */

export const EMAIL_COST = 1;
export const PHONE_COST = 10;

const STATUS_FOUND = 'found';
const STATUS_NOT_FOUND = 'not_found';

const needsEnrichment = (contact, fieldKey, skipPreviouslyEnriched) => {
  const status = contact?.[`enrichment_status_${fieldKey}`];
  // Forced re-fetch (skip OFF, TD-2311): re-query + re-bill every field,
  // including already found / not_found — the BE reserves and bills them.
  if (!skipPreviouslyEnriched) return true;
  // Skip ON: already-resolved fields (found, or cached not_found) are free.
  return status !== STATUS_FOUND && status !== STATUS_NOT_FOUND;
};

export const computeRevealCost = (selectedContacts, skipPreviouslyEnriched) => {
  let email = 0;
  let phone = 0;
  for (const contact of selectedContacts || []) {
    if (needsEnrichment(contact, 'email', skipPreviouslyEnriched)) {
      email += EMAIL_COST;
    }
    if (needsEnrichment(contact, 'phone', skipPreviouslyEnriched)) {
      phone += PHONE_COST;
    }
  }
  return { email, phone, both: email + phone };
};
